import { supabase } from '../lib/supabase';

export const projectTemplateService = {
  // Get all templates
  async getAllTemplates() {
    const { data, error } = await supabase
      ?.from('project_templates')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('usage_count', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(template => ({
      id: template?.id,
      templateName: template?.template_name,
      projectType: template?.project_type,
      description: template?.description,
      defaultTotalObra: template?.default_total_obra,
      defaultValorJaFaturado: template?.default_valor_ja_faturado,
      defaultCustoEmpreiteiro: template?.default_custo_empreiteiro,
      defaultEquipeMensalPrestadores: template?.default_equipe_mensal_prestadores,
      defaultVisitasObraTotal: template?.default_visitas_obra_total,
      defaultOutrosCustosDiretos: template?.default_outros_custos_diretos,
      defaultFixosMensaisEscritorio: template?.default_fixos_mensais_escritorio,
      defaultDuracaoSemanas: template?.default_duracao_semanas,
      defaultDuracaoMeses: template?.default_duracao_meses,
      defaultDasPercentualEfetivo: template?.default_das_percentual_efetivo,
      defaultDasBaseCalculo: template?.default_das_base_calculo,
      defaultProlaboreMensal: template?.default_prolabore_mensal,
      defaultInssPercentualSeg: template?.default_inss_percentual_seg,
      usageCount: template?.usage_count,
      isActive: template?.is_active,
      createdBy: template?.created_by,
      createdAt: template?.created_at,
      updatedAt: template?.updated_at
    }));
  },

  // Get template by ID
  async getTemplateById(id) {
    const { data, error } = await supabase
      ?.from('project_templates')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      templateName: data?.template_name,
      projectType: data?.project_type,
      description: data?.description,
      defaultTotalObra: data?.default_total_obra,
      defaultValorJaFaturado: data?.default_valor_ja_faturado,
      defaultCustoEmpreiteiro: data?.default_custo_empreiteiro,
      defaultEquipeMensalPrestadores: data?.default_equipe_mensal_prestadores,
      defaultVisitasObraTotal: data?.default_visitas_obra_total,
      defaultOutrosCustosDiretos: data?.default_outros_custos_diretos,
      defaultFixosMensaisEscritorio: data?.default_fixos_mensais_escritorio,
      defaultDuracaoSemanas: data?.default_duracao_semanas,
      defaultDuracaoMeses: data?.default_duracao_meses,
      defaultDasPercentualEfetivo: data?.default_das_percentual_efetivo,
      defaultDasBaseCalculo: data?.default_das_base_calculo,
      defaultProlaboreMensal: data?.default_prolabore_mensal,
      defaultInssPercentualSeg: data?.default_inss_percentual_seg,
      usageCount: data?.usage_count,
      isActive: data?.is_active,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Create new template
  async createTemplate(template) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    const { data, error } = await supabase
      ?.from('project_templates')
      ?.insert({
        template_name: template?.templateName,
        project_type: template?.projectType,
        description: template?.description,
        default_total_obra: template?.defaultTotalObra,
        default_valor_ja_faturado: template?.defaultValorJaFaturado,
        default_custo_empreiteiro: template?.defaultCustoEmpreiteiro,
        default_equipe_mensal_prestadores: template?.defaultEquipeMensalPrestadores,
        default_visitas_obra_total: template?.defaultVisitasObraTotal,
        default_outros_custos_diretos: template?.defaultOutrosCustosDiretos,
        default_fixos_mensais_escritorio: template?.defaultFixosMensaisEscritorio,
        default_duracao_semanas: template?.defaultDuracaoSemanas,
        default_duracao_meses: template?.defaultDuracaoMeses,
        default_das_percentual_efetivo: template?.defaultDasPercentualEfetivo,
        default_das_base_calculo: template?.defaultDasBaseCalculo,
        default_prolabore_mensal: template?.defaultProlaboreMensal,
        default_inss_percentual_seg: template?.defaultInssPercentualSeg,
        created_by: user?.id
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Update template
  async updateTemplate(id, template) {
    const { data, error } = await supabase
      ?.from('project_templates')
      ?.update({
        template_name: template?.templateName,
        project_type: template?.projectType,
        description: template?.description,
        default_total_obra: template?.defaultTotalObra,
        default_valor_ja_faturado: template?.defaultValorJaFaturado,
        default_custo_empreiteiro: template?.defaultCustoEmpreiteiro,
        default_equipe_mensal_prestadores: template?.defaultEquipeMensalPrestadores,
        default_visitas_obra_total: template?.defaultVisitasObraTotal,
        default_outros_custos_diretos: template?.defaultOutrosCustosDiretos,
        default_fixos_mensais_escritorio: template?.defaultFixosMensaisEscritorio,
        default_duracao_semanas: template?.defaultDuracaoSemanas,
        default_duracao_meses: template?.defaultDuracaoMeses,
        default_das_percentual_efetivo: template?.defaultDasPercentualEfetivo,
        default_das_base_calculo: template?.defaultDasBaseCalculo,
        default_prolabore_mensal: template?.defaultProlaboreMensal,
        default_inss_percentual_seg: template?.defaultInssPercentualSeg
      })
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Delete template (soft delete)
  async deleteTemplate(id) {
    const { error } = await supabase
      ?.from('project_templates')
      ?.update({ is_active: false })
      ?.eq('id', id);
    
    if (error) throw error;
  },

  // Duplicate template
  async duplicateTemplate(id) {
    const template = await this.getTemplateById(id);
    
    const newTemplate = {
      ...template,
      templateName: `${template?.templateName} (Cópia)`,
      id: undefined,
      createdAt: undefined,
      updatedAt: undefined,
      usageCount: 0
    };
    
    return await this.createTemplate(newTemplate);
  },

  // Increment usage count
  async incrementUsageCount(id) {
    const { error } = await supabase?.rpc('increment_template_usage', { template_id: id });
    
    if (error) {
      // Fallback if RPC doesn't exist
      const template = await this.getTemplateById(id);
      await supabase
        ?.from('project_templates')
        ?.update({ usage_count: (template?.usageCount || 0) + 1 })
        ?.eq('id', id);
    }
  },

  // Get templates by project type
  async getTemplatesByType(projectType) {
    const { data, error } = await supabase
      ?.from('project_templates')
      ?.select('*')
      ?.eq('project_type', projectType)
      ?.eq('is_active', true)
      ?.order('usage_count', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(template => ({
      id: template?.id,
      templateName: template?.template_name,
      projectType: template?.project_type,
      description: template?.description,
      usageCount: template?.usage_count
    }));
  }
};